import React, { useState, useEffect, useCallback } from 'react';

const API = 'https://api.botanix.in/api/ipm';
const HORT_API = 'https://api.botanix.in/api/horticulture';

const C = {
  accent:'#3AAF58', accentLight:'#EAF7EE', accentDark:'#2E8B47',
  bg:'#F3F6F3', surface:'#FFFFFF',
  border:'#E2E8E2', border2:'#CDD8CD',
  text:'#1A2E1E', text2:'#3D5E44', text3:'#7A9A80',
  amber:'#D4840F', amberBg:'#FEF6E7', amberBorder:'#F0D499',
  red:'#C83333', redBg:'#FDEAEA', redBorder:'#F0B8B8',
  blue:'#2563A8', blueBg:'#EAF1FB', blueBorder:'#B8D0F0',
  purple:'#6B48A8', purpleBg:'#F0EBFB', purpleBorder:'#C8B8F0',
  teal:'#1A7A7A', tealBg:'#E7F5F5', tealBorder:'#A8D8D8',
};

const inr = n => 'Rs.' + Number(n||0).toLocaleString('en-IN', { maximumFractionDigits:0 });
const today = () => new Date().toISOString().slice(0,10);

const Card = ({ children, style={} }) => (
  <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:12, boxShadow:'0 1px 4px rgba(0,0,0,.05)', ...style }}>{children}</div>
);

const Btn = ({ children, onClick, variant='primary', style={}, disabled }) => {
  const v = {
    primary:{ background:C.accent, color:'#fff', border:'none' },
    danger: { background:C.red, color:'#fff', border:'none' },
    ghost:  { background:'transparent', color:C.text2, border:`1px solid ${C.border2}` },
    light:  { background:C.accentLight, color:C.accent, border:'none' },
  };
  return <button onClick={onClick} disabled={disabled}
    style={{ ...v[variant], padding:'8px 16px', borderRadius:8, fontSize:13, fontWeight:600,
      cursor:disabled?'not-allowed':'pointer', opacity:disabled?0.5:1,
      display:'inline-flex', alignItems:'center', gap:6, whiteSpace:'nowrap', ...style }}>{children}</button>;
};

const Inp = ({ label, value, onChange, type='text', opts, placeholder='' }) => {
  const base = { width:'100%', padding:'9px 12px', background:C.bg, border:`1px solid ${C.border2}`, borderRadius:8, color:C.text, fontSize:13, boxSizing:'border-box' };
  return (
    <div style={{ marginBottom:12 }}>
      {label && <div style={{ fontSize:11, fontWeight:700, color:C.text3, textTransform:'uppercase', letterSpacing:'.05em', marginBottom:5 }}>{label}</div>}
      {opts
        ? <select style={base} value={value} onChange={e=>onChange(e.target.value)}>
            {opts.map(o=><option key={o.value||o} value={o.value||o}>{o.label||o}</option>)}
          </select>
        : <input type={type} style={base} value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder} />}
    </div>
  );
};

const Modal = ({ title, onClose, children, width='520px' }) => (
  <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.45)', zIndex:200, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
    <Card style={{ width, maxWidth:'100%', maxHeight:'90vh', overflow:'auto', padding:28 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
        <div style={{ fontSize:17, fontWeight:800, color:C.text }}>{title}</div>
        <button onClick={onClose} style={{ background:C.bg, border:`1px solid ${C.border}`, borderRadius:8, width:32, height:32, cursor:'pointer', color:C.text2, fontSize:14 }}>X</button>
      </div>
      {children}
    </Card>
  </div>
);

const Th = ({ c }) => <th style={{ padding:'10px 14px', fontSize:11, fontWeight:700, color:C.text3, textTransform:'uppercase', letterSpacing:'.06em', textAlign:'left', background:'#F5F8F5', borderBottom:`1px solid ${C.border}`, whiteSpace:'nowrap' }}>{c}</th>;
const Td = ({ children, style={} }) => <td style={{ padding:'11px 14px', fontSize:13, borderBottom:`1px solid ${C.border}`, verticalAlign:'middle', ...style }}>{children}</td>;
const Empty = ({ msg }) => <div style={{ padding:'40px 20px', textAlign:'center', color:C.text3, fontSize:14 }}>{msg}</div>;

const riskColor = r => r==='high'?[C.red,C.redBg,C.redBorder]:r==='medium'?[C.amber,C.amberBg,C.amberBorder]:r==='low'?[C.blue,C.blueBg,C.blueBorder]:[C.text3,C.border,C.border2];

const RiskBadge = ({ level, score }) => {
  const [col,bg,brd] = riskColor(level);
  return (
    <span style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'3px 12px', borderRadius:99, fontSize:12, fontWeight:700, color:col, background:bg, border:`1px solid ${brd}` }}>
      <span style={{ width:6, height:6, borderRadius:'50%', background:col }} />
      {level.toUpperCase()} {score !== undefined && `(${score}%)`}
    </span>
  );
};

// ── WEATHER + DISEASE PREDICTOR ───────────────────────────────────────────
function WeatherDiseaseTab() {
  const [weather, setWeather] = useState(null);
  const [risks, setRisks] = useState([]);
  const [forecast, setForecast] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastFetch, setLastFetch] = useState(null);
  const [filterLevel, setFilterLevel] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [wr, fc] = await Promise.all([
        fetch(`${API}/weather`).then(r=>r.json()),
        fetch(`${API}/forecast`).then(r=>r.json()),
      ]);
      setWeather(wr.weather);
      setRisks(wr.risks||[]);
      setForecast(Array.isArray(fc)?fc:[]);
      setLastFetch(new Date().toLocaleTimeString('en-IN'));
    } catch(e) {}
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = filterLevel==='all' ? risks : risks.filter(r=>r.level===filterLevel);
  const highCount = risks.filter(r=>r.level==='high').length;
  const medCount = risks.filter(r=>r.level==='medium').length;

  const windOk = weather && weather.wind_speed < 15;
  const rainOk = weather && weather.rainfall_1h < 0.5;
  const sprayOk = windOk && rainOk;

  return (
    <div style={{ padding:'24px 28px' }}>
      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
        <div>
          <div style={{ fontSize:20, fontWeight:800, color:C.text }}>Weather-Driven Disease Prediction</div>
          <div style={{ fontSize:12, color:C.text3, marginTop:3 }}>Sohna, Gurgaon - Live data from OpenWeatherMap</div>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          {lastFetch && <div style={{ fontSize:12, color:C.text3 }}>Updated {lastFetch}</div>}
          <Btn onClick={load} disabled={loading} variant="light" style={{ padding:'6px 14px', fontSize:12 }}>
            {loading ? 'Refreshing...' : 'Refresh'}
          </Btn>
        </div>
      </div>

      {loading ? (
        <Card style={{ padding:60, textAlign:'center', color:C.text3 }}>Fetching live weather data...</Card>
      ) : weather ? (
        <>
          {/* Weather Panel */}
          <Card style={{ padding:24, marginBottom:16, background:'linear-gradient(135deg,#0A1A2A,#0F2040)', border:'none' }}>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>
              <div>
                <div style={{ fontSize:13, color:'rgba(255,255,255,.5)', marginBottom:8, textTransform:'uppercase', letterSpacing:'.08em' }}>Current Conditions - Sohna</div>
                <div style={{ display:'flex', alignItems:'flex-end', gap:12, marginBottom:16 }}>
                  <div style={{ fontSize:52, fontWeight:800, color:'#fff', lineHeight:1 }}>{Math.round(weather.temp)}C</div>
                  <div style={{ fontSize:14, color:'rgba(255,255,255,.6)', paddingBottom:6 }}>{weather.weather_desc}</div>
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                  {[
                    ['Humidity', `${weather.humidity}%`],
                    ['Dew Point', `${Math.round(weather.dew_point)}C`],
                    ['Wind', `${weather.wind_speed} m/s`],
                    ['Cloud Cover', `${weather.cloud_cover}%`],
                    ['Rainfall 1h', `${weather.rainfall_1h} mm`],
                    ['Visibility', `${(weather.visibility/1000).toFixed(1)} km`],
                  ].map(([l,v]) => (
                    <div key={l} style={{ padding:'8px 12px', background:'rgba(255,255,255,.07)', borderRadius:8 }}>
                      <div style={{ fontSize:10, color:'rgba(255,255,255,.4)', marginBottom:2 }}>{l}</div>
                      <div style={{ fontSize:14, fontWeight:700, color:'#fff' }}>{v}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <div style={{ fontSize:13, color:'rgba(255,255,255,.5)', marginBottom:12, textTransform:'uppercase', letterSpacing:'.08em' }}>Spray Window Check</div>
                <div style={{ padding:'16px 18px', background: sprayOk?'rgba(58,175,88,.2)':'rgba(200,51,51,.2)',
                  border:`1px solid ${sprayOk?'rgba(58,175,88,.4)':'rgba(200,51,51,.4)'}`, borderRadius:12, marginBottom:14 }}>
                  <div style={{ fontSize:16, fontWeight:800, color: sprayOk?'#3AAF58':'#E05050', marginBottom:8 }}>
                    {sprayOk ? 'GOOD TO SPRAY' : 'DO NOT SPRAY'}
                  </div>
                  <div style={{ fontSize:12, color:'rgba(255,255,255,.6)', lineHeight:1.6 }}>
                    Wind: {weather.wind_speed} m/s {windOk?'(OK, under 15)':'(Too strong)'}<br/>
                    Rain: {weather.rainfall_1h}mm/h {rainOk?'(OK, dry)':'(Raining - wait)'}<br/>
                    Best time: {weather.temp > 30 ? 'Early morning or after 5pm' : 'Morning 7-10am'}
                  </div>
                </div>
                <div style={{ fontSize:12, color:'rgba(255,255,255,.5)', marginBottom:8 }}>Disease Risk Summary</div>
                <div style={{ display:'flex', gap:10 }}>
                  <div style={{ flex:1, padding:'12px', background:'rgba(200,51,51,.2)', borderRadius:8, textAlign:'center' }}>
                    <div style={{ fontSize:24, fontWeight:800, color:'#E05050' }}>{highCount}</div>
                    <div style={{ fontSize:11, color:'rgba(255,255,255,.5)' }}>High Risk</div>
                  </div>
                  <div style={{ flex:1, padding:'12px', background:'rgba(212,132,15,.2)', borderRadius:8, textAlign:'center' }}>
                    <div style={{ fontSize:24, fontWeight:800, color:'#F59E0B' }}>{medCount}</div>
                    <div style={{ fontSize:11, color:'rgba(255,255,255,.5)' }}>Medium Risk</div>
                  </div>
                  <div style={{ flex:1, padding:'12px', background:'rgba(58,175,88,.2)', borderRadius:8, textAlign:'center' }}>
                    <div style={{ fontSize:24, fontWeight:800, color:'#3AAF58' }}>{risks.length - highCount - medCount}</div>
                    <div style={{ fontSize:11, color:'rgba(255,255,255,.5)' }}>Low/Minimal</div>
                  </div>
                </div>
              </div>
            </div>
          </Card>

          {/* 5-Day Forecast */}
          {forecast.length > 0 && (
            <Card style={{ padding:20, marginBottom:16 }}>
              <div style={{ fontSize:15, fontWeight:800, color:C.text, marginBottom:14 }}>5-Day Risk Forecast</div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:10 }}>
                {forecast.map(day => (
                  <div key={day.date} style={{ padding:'14px 12px', borderRadius:10, textAlign:'center',
                    background: day.risk_count >= 3 ? C.redBg : day.risk_count >= 1 ? C.amberBg : C.accentLight,
                    border:`1px solid ${day.risk_count >= 3 ? C.redBorder : day.risk_count >= 1 ? C.amberBorder : '#A8D8B8'}` }}>
                    <div style={{ fontSize:11, fontWeight:700, color:C.text3, marginBottom:6 }}>
                      {new Date(day.date).toLocaleDateString('en-IN',{weekday:'short',day:'numeric'})}
                    </div>
                    <div style={{ fontSize:16, fontWeight:800, color:C.text, marginBottom:4 }}>
                      {Math.round(day.temp_max)}C
                    </div>
                    <div style={{ fontSize:11, color:C.text3, marginBottom:8 }}>{Math.round(day.temp_min)}C / {day.avg_humidity}% RH</div>
                    {day.rainfall > 0 && <div style={{ fontSize:11, color:C.blue, marginBottom:6 }}>{day.rainfall}mm rain</div>}
                    <div style={{ fontSize:12, fontWeight:700, color: day.risk_count >= 3 ? C.red : day.risk_count >= 1 ? C.amber : C.accent }}>
                      {day.risk_count} risk{day.risk_count !== 1 ? 's' : ''}
                    </div>
                    {day.top_risk && <div style={{ fontSize:10, color:C.text3, marginTop:4 }}>{day.top_risk.disease.split('(')[0].slice(0,20)}</div>}
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Disease Risk Table */}
          <Card>
            <div style={{ padding:'14px 18px', borderBottom:`1px solid ${C.border}`, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <div style={{ fontSize:15, fontWeight:800, color:C.text }}>Disease Risk Assessment - All Crops</div>
              <div style={{ display:'flex', gap:8 }}>
                {['all','high','medium','low','minimal'].map(l => (
                  <button key={l} onClick={()=>setFilterLevel(l)}
                    style={{ padding:'5px 12px', borderRadius:7, border:`1px solid ${filterLevel===l?C.accent:C.border2}`,
                      background:filterLevel===l?C.accentLight:'transparent', color:filterLevel===l?C.accent:C.text3,
                      fontSize:12, fontWeight:600, cursor:'pointer' }}>
                    {l.charAt(0).toUpperCase()+l.slice(1)}
                  </button>
                ))}
              </div>
            </div>
            <div style={{ overflowX:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead><tr><Th c="Crop" /><Th c="Disease" /><Th c="Risk Score" /><Th c="Level" /><Th c="Why Now" /><Th c="Organic Treatment" /></tr></thead>
                <tbody>
                  {filtered.map((r,i) => (
                    <tr key={i} style={{ background: r.level==='high'?'#FFF5F5':r.level==='medium'?'#FFFBF0':'transparent' }}>
                      <Td><span style={{ fontWeight:700, color:C.text }}>{r.plant}</span></Td>
                      <Td><span style={{ fontWeight:600, fontSize:12 }}>{r.disease}</span></Td>
                      <Td>
                        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                          <div style={{ width:60, height:6, background:C.border, borderRadius:99, overflow:'hidden' }}>
                            <div style={{ height:6, width:`${r.score}%`, borderRadius:99,
                              background: r.score>=70?C.red:r.score>=40?C.amber:C.accent }} />
                          </div>
                          <span style={{ fontSize:13, fontWeight:700, color: r.score>=70?C.red:r.score>=40?C.amber:C.accent }}>{r.score}%</span>
                        </div>
                      </Td>
                      <Td><RiskBadge level={r.level} /></Td>
                      <Td style={{ fontSize:12, color:C.text3, maxWidth:200 }}>{r.trigger_desc}</Td>
                      <Td style={{ fontSize:12, color:C.text2, maxWidth:250 }}>{r.treatment}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      ) : (
        <Card style={{ padding:40, textAlign:'center', color:C.red }}>Failed to load weather data. Check API key.</Card>
      )}
    </div>
  );
}

// ── IPM TREATMENT LOG ─────────────────────────────────────────────────────
function TreatmentsTab({ zones }) {
  const [treatments, setTreatments] = useState([]);
  const [library, setLibrary] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedLib, setSelectedLib] = useState(null);
  const [form, setForm] = useState({ zone_id:'', plant_name:'', pest_or_disease:'', treatment_name:'', treatment_type:'botanical', dosage:'', dilution:'', method:'spray', applied_by:'', effectiveness_rating:'', notes:'', next_application_date:'' });
  const f = k => v => setForm(p=>({...p,[k]:v}));

  const load = () => {
    fetch(`${API}/treatments`).then(r=>r.json()).then(d=>setTreatments(Array.isArray(d)?d:[])).catch(()=>{});
    fetch(`${API}/library`).then(r=>r.json()).then(d=>setLibrary(Array.isArray(d)?d:[])).catch(()=>{});
  };
  useEffect(() => { load(); }, []);

  const applyLib = (item) => {
    setForm(p=>({ ...p, treatment_name:item.name, treatment_type:item.type, dosage:item.dosage, dilution:item.dilution, pest_or_disease:item.target }));
    setSelectedLib(item);
    setShowModal(true);
  };

  const save = async () => {
    await fetch(`${API}/treatments`, { method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify(form) });
    setShowModal(false); load();
  };

  const typeColor = t => t==='biocontrol'?[C.accent,C.accentLight,'#A8D8B8']:t==='botanical'||t==='botanical_homemade'?[C.teal,C.tealBg,C.tealBorder]:t==='physical'?[C.blue,C.blueBg,C.blueBorder]:t==='concoction'?[C.purple,C.purpleBg,C.purpleBorder]:[C.amber,C.amberBg,C.amberBorder];

  return (
    <div style={{ padding:'24px 28px' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
        <div style={{ fontSize:20, fontWeight:800, color:C.text }}>IPM Treatment Log</div>
        <Btn onClick={()=>{ setSelectedLib(null); setShowModal(true); }}>+ Log Treatment</Btn>
      </div>

      {/* Quick apply from library */}
      <Card style={{ padding:20, marginBottom:16 }}>
        <div style={{ fontSize:14, fontWeight:700, color:C.text, marginBottom:12 }}>Quick Apply from Library</div>
        <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
          {library.slice(0,8).map(item => {
            const [col,bg,brd] = typeColor(item.type);
            return (
              <button key={item.name} onClick={()=>applyLib(item)}
                style={{ padding:'6px 14px', borderRadius:8, border:`1px solid ${brd}`, background:bg, color:col,
                  fontSize:12, fontWeight:600, cursor:'pointer' }}>
                {item.name}
              </button>
            );
          })}
        </div>
      </Card>

      {/* Treatments table */}
      <Card>
        <div style={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead><tr><Th c="Date" /><Th c="Zone" /><Th c="Plant" /><Th c="Treatment" /><Th c="Pest/Disease" /><Th c="Applied By" /><Th c="Weather" /><Th c="Rating" /></tr></thead>
            <tbody>
              {treatments.length===0 ? <tr><td colSpan={8}><Empty msg="No treatments logged yet" /></td></tr>
                : treatments.map(t => {
                  const [col,bg,brd] = typeColor(t.treatment_type);
                  return (
                    <tr key={t.id}>
                      <Td style={{ fontSize:12, color:C.text3 }}>{new Date(t.applied_at).toLocaleDateString('en-IN')}</Td>
                      <Td style={{ color:C.text3 }}>{t.zone_name||'-'}</Td>
                      <Td><span style={{ fontWeight:600 }}>{t.plant_name||'All'}</span></Td>
                      <Td>
                        <div style={{ fontWeight:600 }}>{t.treatment_name}</div>
                        <span style={{ fontSize:11, padding:'1px 8px', borderRadius:99, background:bg, color:col, border:`1px solid ${brd}` }}>{t.treatment_type}</span>
                      </Td>
                      <Td style={{ fontSize:12, color:C.text3 }}>{t.pest_or_disease}</Td>
                      <Td style={{ color:C.text3 }}>{t.applied_by||'-'}</Td>
                      <Td style={{ fontSize:11, color:C.text3 }}>{t.weather_at_application}</Td>
                      <Td>
                        {t.effectiveness_rating ? (
                          <div style={{ display:'flex', gap:2 }}>
                            {[1,2,3,4,5].map(s => (
                              <div key={s} style={{ width:10, height:10, borderRadius:'50%',
                                background: s<=t.effectiveness_rating ? C.accent : C.border }} />
                            ))}
                          </div>
                        ) : <span style={{ color:C.text3, fontSize:12 }}>Not rated</span>}
                      </Td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </Card>

      {showModal && (
        <Modal title="Log Treatment" onClose={()=>setShowModal(false)}>
          {selectedLib && (
            <div style={{ padding:'10px 14px', background:C.accentLight, borderRadius:8, marginBottom:14, fontSize:12, color:C.accent }}>
              Using: {selectedLib.name} - {selectedLib.dosage}
            </div>
          )}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <Inp label="Zone" value={form.zone_id} onChange={f('zone_id')} opts={[{value:'',label:'All Zones'},...zones.map(z=>({value:z.id,label:z.name}))]} />
            <Inp label="Plant Name" value={form.plant_name} onChange={f('plant_name')} placeholder="e.g. Tomato, All Vegetables" />
          </div>
          <Inp label="Treatment Name" value={form.treatment_name} onChange={f('treatment_name')} placeholder="e.g. Neem Oil Spray" />
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <Inp label="Pest or Disease" value={form.pest_or_disease} onChange={f('pest_or_disease')} placeholder="e.g. Aphids, Early Blight" />
            <Inp label="Treatment Type" value={form.treatment_type} onChange={f('treatment_type')} opts={['botanical','biocontrol','physical','concoction','inorganic_approved','home_remedy']} />
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <Inp label="Dosage" value={form.dosage} onChange={f('dosage')} placeholder="e.g. 5ml/L" />
            <Inp label="Dilution" value={form.dilution} onChange={f('dilution')} placeholder="e.g. 1:200" />
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <Inp label="Method" value={form.method} onChange={f('method')} opts={['spray','soil drench','seed treatment','dusting','trapping','manual removal']} />
            <Inp label="Applied By" value={form.applied_by} onChange={f('applied_by')} placeholder="Staff name" />
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <Inp label="Effectiveness (1-5)" value={form.effectiveness_rating} onChange={f('effectiveness_rating')} type="number" placeholder="Rate 1-5 after 3 days" />
            <Inp label="Next Application" value={form.next_application_date} onChange={f('next_application_date')} type="date" />
          </div>
          <Inp label="Notes" value={form.notes} onChange={f('notes')} placeholder="Observations, spray coverage, weather conditions..." />
          <div style={{ display:'flex', gap:10 }}>
            <Btn onClick={save} style={{ flex:1, justifyContent:'center' }}>Save Treatment</Btn>
            <Btn variant="ghost" onClick={()=>setShowModal(false)} style={{ flex:1, justifyContent:'center' }}>Cancel</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── TRAPS ────────────────────────────────────────────────────────────────
function TrapsTab({ zones }) {
  const [traps, setTraps] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [checkModal, setCheckModal] = useState(null);
  const [form, setForm] = useState({ zone_id:'', trap_type:'yellow_sticky', target_pest:'', installed_date:today(), notes:'' });
  const [checkForm, setCheckForm] = useState({ catch_count:'0', notes:'' });
  const f = k => v => setForm(p=>({...p,[k]:v}));

  const load = () => { fetch(`${API}/traps`).then(r=>r.json()).then(d=>setTraps(Array.isArray(d)?d:[])).catch(()=>{}); };
  useEffect(() => { load(); }, []);

  const save = async () => {
    await fetch(`${API}/traps`, { method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify(form) });
    setShowModal(false); load();
  };

  const check = async () => {
    await fetch(`${API}/traps/${checkModal.id}/check`, { method:'PATCH', headers:{'Content-Type':'application/json'}, body:JSON.stringify(checkForm) });
    setCheckModal(null); load();
  };

  const daysSince = d => d ? Math.floor((new Date()-new Date(d))/86400000) : null;

  return (
    <div style={{ padding:'24px 28px' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
        <div style={{ fontSize:20, fontWeight:800, color:C.text }}>Trap Monitoring</div>
        <Btn onClick={()=>setShowModal(true)}>+ Install Trap</Btn>
      </div>
      {traps.length===0 ? <Card style={{ padding:40, textAlign:'center', color:C.text3 }}>No active traps installed</Card> : (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(260px,1fr))', gap:14 }}>
          {traps.map(t => {
            const days = daysSince(t.last_checked);
            const needsCheck = days === null || days >= 3;
            return (
              <Card key={t.id} style={{ padding:20, borderTop:`4px solid ${needsCheck?C.amber:C.accent}` }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:10 }}>
                  <div>
                    <div style={{ fontSize:14, fontWeight:800, color:C.text }}>{t.trap_type.replace('_',' ').toUpperCase()}</div>
                    <div style={{ fontSize:12, color:C.text3 }}>Target: {t.target_pest}</div>
                  </div>
                  {needsCheck && <span style={{ fontSize:11, background:C.amberBg, color:C.amber, border:`1px solid ${C.amberBorder}`, padding:'2px 8px', borderRadius:99, fontWeight:700 }}>CHECK NOW</span>}
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:12 }}>
                  <div style={{ background:C.bg, borderRadius:7, padding:'8px 10px' }}>
                    <div style={{ fontSize:10, color:C.text3 }}>Zone</div>
                    <div style={{ fontSize:12, fontWeight:600 }}>{t.zone_name||'-'}</div>
                  </div>
                  <div style={{ background:C.bg, borderRadius:7, padding:'8px 10px' }}>
                    <div style={{ fontSize:10, color:C.text3 }}>Total Caught</div>
                    <div style={{ fontSize:16, fontWeight:800, color: t.catch_count > 20 ? C.red : t.catch_count > 10 ? C.amber : C.accent }}>{t.catch_count}</div>
                  </div>
                  <div style={{ background:C.bg, borderRadius:7, padding:'8px 10px' }}>
                    <div style={{ fontSize:10, color:C.text3 }}>Installed</div>
                    <div style={{ fontSize:12, fontWeight:600 }}>{t.installed_date?.slice(0,10)}</div>
                  </div>
                  <div style={{ background:C.bg, borderRadius:7, padding:'8px 10px' }}>
                    <div style={{ fontSize:10, color:C.text3 }}>Last Checked</div>
                    <div style={{ fontSize:12, fontWeight:600, color:needsCheck?C.amber:C.text }}>{days===null?'Never':`${days}d ago`}</div>
                  </div>
                </div>
                <Btn onClick={()=>{ setCheckForm({catch_count:'0',notes:''}); setCheckModal(t); }}
                  variant={needsCheck?'primary':'light'} style={{ width:'100%', justifyContent:'center', fontSize:12 }}>
                  Record Check
                </Btn>
              </Card>
            );
          })}
        </div>
      )}

      {showModal && (
        <Modal title="Install Trap" onClose={()=>setShowModal(false)}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <Inp label="Trap Type" value={form.trap_type} onChange={f('trap_type')} opts={[{value:'yellow_sticky',label:'Yellow Sticky Trap'},{value:'pheromone',label:'Pheromone Trap'},{value:'blue_sticky',label:'Blue Sticky Trap'},{value:'light_trap',label:'Light Trap'},{value:'pitfall',label:'Pitfall Trap'}]} />
            <Inp label="Zone" value={form.zone_id} onChange={f('zone_id')} opts={[{value:'',label:'Select zone'},...zones.map(z=>({value:z.id,label:z.name}))]} />
          </div>
          <Inp label="Target Pest" value={form.target_pest} onChange={f('target_pest')} placeholder="e.g. Whitefly, Aphids, Fruit Borer" />
          <Inp label="Installation Date" value={form.installed_date} onChange={f('installed_date')} type="date" />
          <Inp label="Notes" value={form.notes} onChange={f('notes')} placeholder="Location details, height, etc." />
          <div style={{ display:'flex', gap:10 }}>
            <Btn onClick={save} style={{ flex:1, justifyContent:'center' }}>Install</Btn>
            <Btn variant="ghost" onClick={()=>setShowModal(false)} style={{ flex:1, justifyContent:'center' }}>Cancel</Btn>
          </div>
        </Modal>
      )}

      {checkModal && (
        <Modal title={`Check - ${checkModal.trap_type.replace('_',' ')}`} onClose={()=>setCheckModal(null)} width="400px">
          <div style={{ padding:'12px 14px', background:C.bg, borderRadius:8, marginBottom:14 }}>
            <div style={{ fontSize:12, color:C.text3 }}>Zone: {checkModal.zone_name} | Target: {checkModal.target_pest}</div>
            <div style={{ fontSize:13, fontWeight:600, color:C.text, marginTop:4 }}>Total caught so far: {checkModal.catch_count}</div>
          </div>
          <Inp label="New Catches Since Last Check" value={checkForm.catch_count} onChange={v=>setCheckForm(p=>({...p,catch_count:v}))} type="number" placeholder="0" />
          <Inp label="Notes" value={checkForm.notes} onChange={v=>setCheckForm(p=>({...p,notes:v}))} placeholder="Observations..." />
          <div style={{ display:'flex', gap:10 }}>
            <Btn onClick={check} style={{ flex:1, justifyContent:'center' }}>Save Check</Btn>
            <Btn variant="ghost" onClick={()=>setCheckModal(null)} style={{ flex:1, justifyContent:'center' }}>Cancel</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── CONCOCTIONS ───────────────────────────────────────────────────────────
function ConcoctionsTab() {
  const [items, setItems] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ name:'', category:'spray', ingredients:'', preparation_method:'', dosage:'', dilution:'', target_pests:'', notes:'', created_by:'' });
  const f = k => v => setForm(p=>({...p,[k]:v}));

  const load = () => { fetch(`${API}/concoctions`).then(r=>r.json()).then(d=>setItems(Array.isArray(d)?d:[])).catch(()=>{}); };
  useEffect(() => { load(); }, []);

  const save = async () => {
    await fetch(`${API}/concoctions`, { method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify(form) });
    setShowModal(false); load();
  };

  const rate = async (id, rating) => {
    await fetch(`${API}/concoctions/${id}/rate`, { method:'PATCH', headers:{'Content-Type':'application/json'}, body:JSON.stringify({ rating }) });
    load();
  };

  return (
    <div style={{ padding:'24px 28px' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
        <div style={{ fontSize:20, fontWeight:800, color:C.text }}>Botanix Concoctions</div>
        <Btn onClick={()=>setShowModal(true)}>+ Add Recipe</Btn>
      </div>
      <div style={{ fontSize:13, color:C.text3, marginBottom:20 }}>Your proprietary organic treatments — track, rate, and refine what works best at Botanix.</div>

      {items.length===0 ? (
        <Card style={{ padding:40, textAlign:'center' }}>
          <div style={{ fontSize:14, color:C.text3, marginBottom:12 }}>No concoctions added yet</div>
          <div style={{ fontSize:13, color:C.text2 }}>Start documenting your organic recipes — Panchagavya, Jeevamrutha, garlic-chilli sprays, and more.</div>
        </Card>
      ) : (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(320px,1fr))', gap:14 }}>
          {items.map(item => (
            <Card key={item.id} style={{ padding:20 }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:10 }}>
                <div style={{ fontSize:15, fontWeight:800, color:C.text }}>{item.name}</div>
                <span style={{ fontSize:11, background:C.tealBg, color:C.teal, border:`1px solid ${C.tealBorder}`, padding:'2px 8px', borderRadius:99 }}>{item.category}</span>
              </div>
              {item.target_pests && <div style={{ fontSize:12, color:C.text2, marginBottom:8 }}>Targets: {item.target_pests}</div>}
              {item.dosage && <div style={{ fontSize:12, color:C.text3, marginBottom:4 }}>Dosage: {item.dosage} {item.dilution&&`| Dilution: ${item.dilution}`}</div>}
              {item.ingredients && (
                <div style={{ padding:'10px 12px', background:C.bg, borderRadius:8, marginBottom:10 }}>
                  <div style={{ fontSize:10, color:C.text3, marginBottom:4, textTransform:'uppercase', letterSpacing:'.05em' }}>Ingredients</div>
                  <div style={{ fontSize:12, color:C.text2 }}>{item.ingredients}</div>
                </div>
              )}
              {item.preparation_method && (
                <div style={{ fontSize:12, color:C.text3, marginBottom:10 }}>{item.preparation_method.slice(0,120)}{item.preparation_method.length>120?'...':''}</div>
              )}
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:10 }}>
                <div style={{ fontSize:12, color:C.text3 }}>Rate effectiveness:</div>
                <div style={{ display:'flex', gap:4 }}>
                  {[1,2,3,4,5].map(s => (
                    <button key={s} onClick={()=>rate(item.id,s)}
                      style={{ width:24, height:24, borderRadius:'50%', border:'none', cursor:'pointer',
                        background: s <= (item.effectiveness_rating||0) ? C.accent : C.border, color:'#fff', fontSize:11, fontWeight:700 }}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
              {item.effectiveness_rating && (
                <div style={{ fontSize:12, color:C.accent, fontWeight:700, marginTop:6 }}>
                  Avg rating: {item.effectiveness_rating}/5
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      {showModal && (
        <Modal title="Add Concoction Recipe" onClose={()=>setShowModal(false)} width="580px">
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <Inp label="Name" value={form.name} onChange={f('name')} placeholder="e.g. Panchagavya" />
            <Inp label="Category" value={form.category} onChange={f('category')} opts={['spray','soil drench','seed treatment','dust','fumigant','trap crop','other']} />
          </div>
          <Inp label="Ingredients" value={form.ingredients} onChange={f('ingredients')} placeholder="e.g. Cow dung 5kg + cow urine 3L + milk 2L..." />
          <Inp label="Preparation Method" value={form.preparation_method} onChange={f('preparation_method')} placeholder="Step by step preparation instructions..." />
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <Inp label="Dosage" value={form.dosage} onChange={f('dosage')} placeholder="e.g. 30ml/L water" />
            <Inp label="Dilution" value={form.dilution} onChange={f('dilution')} placeholder="e.g. 1:33" />
          </div>
          <Inp label="Target Pests or Diseases" value={form.target_pests} onChange={f('target_pests')} placeholder="e.g. Aphids, Powdery Mildew, General immunity" />
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <Inp label="Created By" value={form.created_by} onChange={f('created_by')} placeholder="Staff name" />
          </div>
          <Inp label="Notes" value={form.notes} onChange={f('notes')} placeholder="When to use, special conditions, storage..." />
          <div style={{ display:'flex', gap:10 }}>
            <Btn onClick={save} style={{ flex:1, justifyContent:'center' }}>Save Recipe</Btn>
            <Btn variant="ghost" onClick={()=>setShowModal(false)} style={{ flex:1, justifyContent:'center' }}>Cancel</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── SPRAY SCHEDULE ────────────────────────────────────────────────────────
function SprayScheduleTab({ zones }) {
  const [schedule, setSchedule] = useState([]);
  const [library, setLibrary] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ zone_id:'', plant_name:'', treatment_name:'', scheduled_date:today(), scheduled_time:'morning' });
  const f = k => v => setForm(p=>({...p,[k]:v}));

  const load = () => {
    fetch(`${API}/schedule`).then(r=>r.json()).then(d=>setSchedule(Array.isArray(d)?d:[])).catch(()=>{});
    fetch(`${API}/library`).then(r=>r.json()).then(d=>setLibrary(Array.isArray(d)?d:[])).catch(()=>{});
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    await fetch(`${API}/schedule`, { method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify(form) });
    setShowModal(false); load();
  };

  const complete = async (id) => {
    await fetch(`${API}/schedule/${id}/complete`, { method:'PATCH' });
    load();
  };

  const statusColor = s => s==='completed'?[C.accent,C.accentLight,'#A8D8B8']:s==='scheduled'?[C.blue,C.blueBg,C.blueBorder]:[C.red,C.redBg,C.redBorder];

  return (
    <div style={{ padding:'24px 28px' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
        <div style={{ fontSize:20, fontWeight:800, color:C.text }}>Spray Schedule</div>
        <Btn onClick={()=>setShowModal(true)}>+ Schedule Spray</Btn>
      </div>

      <Card>
        <div style={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead><tr><Th c="Date" /><Th c="Time" /><Th c="Zone" /><Th c="Plant" /><Th c="Treatment" /><Th c="Status" /><Th c="Weather OK" /><Th c="Action" /></tr></thead>
            <tbody>
              {schedule.length===0 ? <tr><td colSpan={8}><Empty msg="No sprays scheduled" /></td></tr>
                : schedule.map(s => {
                  const [col,bg,brd] = statusColor(s.status);
                  const isOverdue = s.scheduled_date < today() && s.status === 'scheduled';
                  return (
                    <tr key={s.id} style={{ background:isOverdue?'#FFF5F5':'transparent' }}>
                      <Td style={{ fontSize:12, color:isOverdue?C.red:C.text3, fontWeight:isOverdue?700:400 }}>
                        {s.scheduled_date?.slice(0,10)}{isOverdue?' OVERDUE':''}
                      </Td>
                      <Td><span style={{ fontSize:12, background:C.bg, padding:'2px 8px', borderRadius:6 }}>{s.scheduled_time}</span></Td>
                      <Td style={{ color:C.text3 }}>{s.zone_name||'All'}</Td>
                      <Td style={{ fontWeight:600 }}>{s.plant_name||'All Plants'}</Td>
                      <Td><span style={{ fontWeight:600 }}>{s.treatment_name}</span></Td>
                      <Td>
                        <span style={{ fontSize:11, padding:'2px 10px', borderRadius:99, background:bg, color:col, border:`1px solid ${brd}`, fontWeight:600 }}>
                          {s.status}
                        </span>
                      </Td>
                      <Td style={{ textAlign:'center' }}>
                        {s.weather_check_passed === true ? <span style={{ color:C.accent, fontWeight:700 }}>Yes</span>
                          : s.weather_check_passed === false ? <span style={{ color:C.red, fontWeight:700 }}>No</span>
                          : <span style={{ color:C.text3 }}>-</span>}
                      </Td>
                      <Td>
                        {s.status==='scheduled' && (
                          <Btn onClick={()=>complete(s.id)} variant="light" style={{ padding:'4px 12px', fontSize:12 }}>
                            Mark Done
                          </Btn>
                        )}
                      </Td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </Card>

      {showModal && (
        <Modal title="Schedule Spray" onClose={()=>setShowModal(false)}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <Inp label="Zone" value={form.zone_id} onChange={f('zone_id')} opts={[{value:'',label:'All Zones'},...zones.map(z=>({value:z.id,label:z.name}))]} />
            <Inp label="Plant Name" value={form.plant_name} onChange={f('plant_name')} placeholder="e.g. Tomato or All" />
          </div>
          <Inp label="Treatment" value={form.treatment_name} onChange={f('treatment_name')} opts={[{value:'',label:'Select or type below'},...library.map(l=>({value:l.name,label:l.name}))]} />
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <Inp label="Scheduled Date" value={form.scheduled_date} onChange={f('scheduled_date')} type="date" />
            <Inp label="Time of Day" value={form.scheduled_time} onChange={f('scheduled_time')} opts={['early morning','morning','afternoon','evening']} />
          </div>
          <div style={{ padding:'10px 14px', background:C.amberBg, borderRadius:8, marginBottom:14, fontSize:12, color:C.amber }}>
            System will automatically check wind speed and rainfall when marking as done and flag if weather was unsuitable for spraying.
          </div>
          <div style={{ display:'flex', gap:10 }}>
            <Btn onClick={save} style={{ flex:1, justifyContent:'center' }}>Schedule</Btn>
            <Btn variant="ghost" onClick={()=>setShowModal(false)} style={{ flex:1, justifyContent:'center' }}>Cancel</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── EFFECTIVENESS ─────────────────────────────────────────────────────────
function EffectivenessTab() {
  const [data, setData] = useState([]);
  const [library, setLibrary] = useState([]);

  useEffect(() => {
    fetch(`${API}/effectiveness`).then(r=>r.json()).then(d=>setData(Array.isArray(d)?d:[])).catch(()=>{});
    fetch(`${API}/library`).then(r=>r.json()).then(d=>setLibrary(Array.isArray(d)?d:[])).catch(()=>{});
  }, []);

  const typeColor = t => t==='biocontrol'?C.accent:t==='botanical'||t==='botanical_homemade'?C.teal:t==='physical'?C.blue:t==='concoction'?C.purple:C.amber;

  return (
    <div style={{ padding:'24px 28px' }}>
      <div style={{ fontSize:20, fontWeight:800, color:C.text, marginBottom:8 }}>Treatment Effectiveness Analysis</div>
      <div style={{ fontSize:13, color:C.text3, marginBottom:20 }}>AI-ranked treatments based on actual results at Botanix. Rate treatments after application to improve recommendations.</div>

      {data.length > 0 && (
        <Card style={{ padding:20, marginBottom:16 }}>
          <div style={{ fontSize:15, fontWeight:800, color:C.text, marginBottom:14 }}>Your Best Performing Treatments</div>
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            {data.map((t,i) => (
              <div key={i} style={{ display:'flex', alignItems:'center', gap:14, padding:'12px 14px', background:C.bg, borderRadius:10, border:`1px solid ${C.border}` }}>
                <div style={{ fontSize:18, fontWeight:800, color:C.text3, width:28, textAlign:'center' }}>#{i+1}</div>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:14, fontWeight:700, color:C.text }}>{t.treatment_name}</div>
                  <div style={{ fontSize:11, color:typeColor(t.treatment_type), fontWeight:600, marginTop:2 }}>{t.treatment_type}</div>
                </div>
                <div style={{ textAlign:'center', minWidth:60 }}>
                  <div style={{ fontSize:20, fontWeight:800, color:C.accent }}>{t.avg_rating}</div>
                  <div style={{ fontSize:10, color:C.text3 }}>avg rating</div>
                </div>
                <div style={{ textAlign:'center', minWidth:60 }}>
                  <div style={{ fontSize:18, fontWeight:700, color:C.blue }}>{t.applications}</div>
                  <div style={{ fontSize:10, color:C.text3 }}>uses</div>
                </div>
                <div style={{ display:'flex', gap:2 }}>
                  {[1,2,3,4,5].map(s => (
                    <div key={s} style={{ width:10, height:10, borderRadius:'50%', background: s<=parseFloat(t.avg_rating) ? C.accent : C.border }} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Full library */}
      <Card style={{ padding:20 }}>
        <div style={{ fontSize:15, fontWeight:800, color:C.text, marginBottom:14 }}>Complete IPM Library - 16 Organic Treatments</div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(300px,1fr))', gap:12 }}>
          {library.map(item => (
            <div key={item.name} style={{ padding:'14px 16px', background:C.bg, borderRadius:10, border:`1px solid ${C.border}`, borderLeft:`3px solid ${typeColor(item.type)}` }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:8 }}>
                <div style={{ fontSize:13, fontWeight:700, color:C.text }}>{item.name}</div>
                <span style={{ fontSize:10, color:typeColor(item.type), fontWeight:700, background:`${typeColor(item.type)}18`, padding:'2px 8px', borderRadius:99 }}>{item.type}</span>
              </div>
              <div style={{ fontSize:11, color:C.text3, marginBottom:6 }}>Targets: {item.target}</div>
              <div style={{ fontSize:11, color:C.text2, marginBottom:4 }}>Dose: {item.dosage}</div>
              <div style={{ fontSize:11, color:C.text2, marginBottom:4 }}>Frequency: {item.frequency}</div>
              <div style={{ fontSize:11, color:C.text3, fontStyle:'italic', marginTop:6 }}>{item.notes}</div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

// ── MAIN ─────────────────────────────────────────────────────────────────
export default function IPMModule() {
  const [tab, setTab] = useState('weather');
  const [zones, setZones] = useState([]);

  useEffect(() => {
    fetch(`${HORT_API}/zones`).then(r=>r.json()).then(d=>setZones(Array.isArray(d)?d:[])).catch(()=>{});
  }, []);

  const tabs = [
    ['weather', 'Weather + Disease'],
    ['treatments', 'Treatment Log'],
    ['traps', 'Trap Monitoring'],
    ['concoctions', 'Botanix Concoctions'],
    ['schedule', 'Spray Schedule'],
    ['effectiveness', 'What Works Best'],
  ];

  return (
    <div style={{ background:C.bg, minHeight:'100vh' }}>
      <div style={{ background:C.surface, borderBottom:`1px solid ${C.border}`, padding:'0 28px', position:'sticky', top:0, zIndex:50 }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', height:60 }}>
          <div style={{ fontSize:20, fontWeight:800, color:C.text }}>IPM - Integrated Pest Management</div>
          <div style={{ fontSize:12, color:C.text3 }}>Organic methods only - Sohna, Gurgaon</div>
        </div>
        <div style={{ display:'flex', gap:2, overflowX:'auto' }}>
          {tabs.map(([id, label]) => (
            <button key={id} onClick={()=>setTab(id)}
              style={{ padding:'10px 18px', border:'none', background:'transparent',
                color: tab===id?C.accent:C.text3, fontSize:13, fontWeight:tab===id?700:500,
                cursor:'pointer', whiteSpace:'nowrap',
                borderBottom: tab===id?`2px solid ${C.accent}`:'2px solid transparent' }}>
              {label}
            </button>
          ))}
        </div>
      </div>
      {tab==='weather' && <WeatherDiseaseTab />}
      {tab==='treatments' && <TreatmentsTab zones={zones} />}
      {tab==='traps' && <TrapsTab zones={zones} />}
      {tab==='concoctions' && <ConcoctionsTab />}
      {tab==='schedule' && <SprayScheduleTab zones={zones} />}
      {tab==='effectiveness' && <EffectivenessTab />}
    </div>
  );
}
